/**
 * Section display type used in apartment editor and GA rendering.
 */
export type CustomSectionServiceType = 'info' | 'upsell';

/**
 * Built-in stay section identifiers persisted in config and section order lists.
 */
export type BuiltinStaySectionId = 'parking' | 'breakfast' | 'cleaning' | 'earlyCheckIn' | 'lateCheckOut';

export const BUILTIN_STAY_SECTION_IDS: BuiltinStaySectionId[] = [
  'parking',
  'breakfast',
  'cleaning',
  'earlyCheckIn',
  'lateCheckOut',
];

export const BUILTIN_SECTIONS_WITH_PERSISTED_SERVICE_TYPE: BuiltinStaySectionId[] = [
  'parking',
  'breakfast',
  'cleaning',
];

export type StaticStaySectionId = 'amenities' | 'transfer';

export const STATIC_STAY_SECTION_IDS: StaticStaySectionId[] = ['amenities', 'transfer'];

/**
 * Static info-only section identifiers used in stay (info) ordering.
 */
export type StaticStayInfoSectionId = 'directions' | StaticStaySectionId;

/**
 * Any section identifier that can appear in config.sectionsOrder lists.
 */
export type ApartmentSectionOrderId = BuiltinStaySectionId | StaticStayInfoSectionId | `custom:${string}`;

/**
 * Persisted section ordering for GA rendering.
 * Stored in room.config.sectionsOrder.
 */
export interface IApartmentSectionsOrder {
  stay?: ApartmentSectionOrderId[];
  upsell?: ApartmentSectionOrderId[];
}

/**
 * Presentation config for built-in stay sections.
 * Language-independent: serviceType, cover, active.
 * Language-specific display names: displayNames[lang] (legacy displayName as read fallback).
 * Stored in room.config.builtinSections.
 */
export interface IBuiltinStaySectionConfig {
  serviceType?: CustomSectionServiceType;
  cover?: string | null;
  /** @deprecated Use displayNames. Kept for legacy documents and read-time fallback. */
  displayName?: string;
  displayNames?: Partial<Record<string, string>>;
  active?: boolean;
}

export type IBuiltinStaySectionsConfig = Partial<Record<BuiltinStaySectionId, IBuiltinStaySectionConfig>>;

export interface IStaticStaySectionConfig {
  active?: boolean;
}

export type IStaticStaySectionsConfig = Partial<Record<StaticStaySectionId, IStaticStaySectionConfig>>;

/**
 * Any stay section identifier (built-in or static) usable as a key in the editor's staySections map.
 */
export type StaySectionId = BuiltinStaySectionId | StaticStaySectionId;

/**
 * Unified, per-language editor shape for a single stay section.
 * Replaces the previously flat per-section fields on IApartmentEditor.
 * Mongo persistence stays scattered; the adapter translates to/from this shape.
 * displayName carries only the active language value (editing is per language);
 * the persisted multi-language displayNames map lives only in IBuiltinStaySectionConfig.
 */
export interface IStaySectionEditor {
  /** Built-in sections only; static sections are always 'info'. */
  serviceType?: CustomSectionServiceType;
  /** Rich description for parking/breakfast/cleaning, early/late info, or transfer name. */
  description?: string;
  /** Amenities only: keeps string or string[] behaviour. */
  amenities?: string | string[];
  /** parking/breakfast/cleaning + early/late price. */
  price?: string | number;
  /** early/late currency (legacy; merged into price by data migration). */
  currency?: string;
  /** early/late only: drives upsell (apartmentInfo.stayAdjustments.*.enabled). */
  enabled?: boolean;
  /** Highlight on home screen (config.orderButtons.{id}). */
  highlight?: boolean;
  /** Section visibility (builtinSections.active or staticStaySections.active). */
  active?: boolean;
  /** Built-in upsell cover image. */
  cover?: string | null;
  /** Display name for the active language only. */
  displayName?: string;
  /** transfer only: pickup service phone. */
  phone?: string | number;
}

export type IStaySectionsEditor = Partial<Record<StaySectionId, IStaySectionEditor>>;
